/**
 * Checklist 节点视图组件
 * 用于渲染 Lexical ChecklistNode
 */

import { Check, Square } from "lucide-react";
import React from "react";
import type { ChecklistData } from "@/components/Editor/nodes/types";
import { cn } from "@/lib/utils";

interface ChecklistViewProps {
  data: ChecklistData;
  className?: string;
}

export const ChecklistView: React.FC<ChecklistViewProps> = ({ data, className }) => {
  const { items, title } = data;

  // 防御性检查：items 可能不存在
  if (!items || !Array.isArray(items)) {
    return (
      <div className={cn("my-4 p-4 rounded-lg border bg-card", "checklist-node", className)}>
        <div className="text-sm text-muted-foreground">Checklist 数据格式错误</div>
      </div>
    );
  }

  // 按类别分组
  const groupedItems = React.useMemo(() => {
    const groups: Record<string, typeof items> = {};
    items.forEach((item) => {
      const category = item.category || "默认";
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(item);
    });
    return groups;
  }, [items]);

  const completedCount = items.filter((item) => item.checked).length;
  const totalCount = items.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <div className={cn("my-4 p-4 rounded-lg border bg-card", "checklist-node", className)}>
      {/* 标题和进度 */}
      <div className="flex items-center justify-between mb-3">
        {title && (
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold">📋</span>
            <span className="font-semibold text-base">{title}</span>
          </div>
        )}
        <div className="text-sm text-muted-foreground">
          {completedCount}/{totalCount} 已完成
        </div>
      </div>

      {/* 进度条 */}
      <div className="mb-4">
        <div className="w-full bg-secondary rounded-full h-2">
          <div className="bg-primary h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Checklist 项 */}
      <div className="space-y-3">
        {Object.entries(groupedItems).map(([category, categoryItems]) => (
          <div key={category}>
            {category !== "默认" && <div className="text-sm font-medium text-muted-foreground mb-2">{category}</div>}
            <div className="space-y-2">
              {categoryItems.map((item, index) => (
                <div
                  key={index}
                  className={cn(
                    "flex items-start gap-3 p-2 rounded-md transition-colors",
                    item.checked ? "bg-muted/30" : "bg-secondary/20 hover:bg-secondary/30",
                  )}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    {item.checked ? <Check className="w-5 h-5 text-primary" /> : <Square className="w-5 h-5 text-muted-foreground" />}
                  </div>
                  <div className={cn("flex-1 text-sm", item.checked && "line-through text-muted-foreground")}>{item.text}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChecklistView;
